import {StrictMode, useEffect, useMemo} from 'react';
import {view, RootLayer} from '@sail/ui';
import {Provider as SailClassicProvider} from '@sail/theme-classic';
import {IntlProvider} from 'react-intl';
import {MessageFormatElement} from 'intl-messageformat-parser';
import {captureException} from '@sentry/browser';

import type {
  AppConfig,
  AppProfile,
} from '@stripe-internal/conversations-assistant';
import {
  isDotComEntryPoint,
  StripeAssistant,
  ThreadsAssistant,
} from '@stripe-internal/conversations-assistant';
import {DotComAssistant} from '@stripe-internal/dotcom-conversations-assistant';
import {MobileAssistant} from '@stripe-internal/mobile-conversations-assistant';

import {DefaultInlineDataComponentRegistry} from '@stripe-internal/mse-inline-data';
import type {Config} from '@stripe-internal/conversations-shared-dependencies';
import {
  ManagedMessenger,
  targetContextFromWindow,
  TRUST_LEVELS,
  CommsObservabilityLogger,
  CommsObservabilityMetrics,
  object,
} from '@sail/comms';
import {
  ObservabilityProvider,
  useObservability,
} from './Providers/ObservabilityProvider';
import {buildConfig} from './buildExtractedConfig';
import {supportFetch} from './helpers/supportFetch';
import {useConvUITheme} from './Providers/AppThemeProvider';
import {isIframed} from './helpers/isIframed';

const activitySchema = object({});
const ACTIVITY_COMMS_ID = 'support-site-express-inactivity-timeout-v1';

type Props = {
  locale?: string;
  messages?: Record<string, string> | Record<string, MessageFormatElement[]>;
  config: Config;
};

function AppByEntryPoint({
  config,
  profile,
}: {
  config: AppConfig;
  profile: AppProfile;
}) {
  const observability = useObservability();

  useEffect(() => {
    if (!profile.featureFlags.useExtractedConversationsAssistant) {
      captureException('Running extracted app without feature flag enabled', {
        extra: {
          entryPoint: config.entryPoint,
        },
      });
    }
  }, [config, profile]);

  const flags = profile.featureFlags as Record<string, unknown>;
  const enableExpressInactivityTimeout = !!flags.enableExpressInactivityTimeout;
  const enableExpressInactivityTimeoutSupportSite =
    !!flags.enableExpressInactivityTimeoutSupportSite;

  useEffect(() => {
    if (
      !isIframed() ||
      !enableExpressInactivityTimeout ||
      !enableExpressInactivityTimeoutSupportSite
    ) {
      return;
    }

    const parentOriginStr = (() => {
      try {
        return new URL(document.referrer).origin;
      } catch {
        return null;
      }
    })();
    if (!parentOriginStr) return;

    const messenger = new ManagedMessenger({
      remote: {
        context: targetContextFromWindow(window.parent as WindowProxy),
        trustLevel: TRUST_LEVELS.TRUSTED,
        origin: parentOriginStr,
      },
      self: {
        context: targetContextFromWindow(window),
        trustLevel: TRUST_LEVELS.TRUSTED,
      },
      logger: new CommsObservabilityLogger(),
      metrics: new CommsObservabilityMetrics(),
      schemasByType: {stripe_support_activity: activitySchema},
      communicationId: ACTIVITY_COMMS_ID,
    });

    let lastNotifyTs = 0;
    const NOTIFY_THROTTLE_MS = 5000;
    const notifyParent = () => {
      const now = Date.now();
      if (now - lastNotifyTs < NOTIFY_THROTTLE_MS) {
        return;
      }
      lastNotifyTs = now;
      messenger.sendMessage('stripe_support_activity', {});
    };

    const events = [
      'mousemove',
      'mousedown',
      'mouseup',
      'click',
      'keydown',
      'keyup',
      'touchstart',
      'touchmove',
      'touchend',
      'touchcancel',
      'input',
      'scroll',
      'wheel',
      'pointerdown',
      'pointermove',
      'pointerup',
    ] as const;

    events.forEach((e) =>
      document.addEventListener(e, notifyParent, {
        passive: true,
        capture: true,
      }),
    );
    return () => {
      events.forEach((e) =>
        document.removeEventListener(e, notifyParent, {capture: true}),
      );
      messenger.destroy();
    };
  }, [
    enableExpressInactivityTimeout,
    enableExpressInactivityTimeoutSupportSite,
  ]);

  const mobileEntryPoints = [
    'mobile_ios',
    'mobile_android',
    'react_native_mobile_ios',
    'react_native_mobile_android',
    'mobile_link_ios',
    'mobile_link_android',
  ];

  if (mobileEntryPoints.includes(config.entryPoint)) {
    return (
      <MobileAssistant
        config={config}
        profile={profile}
        observability={observability}
        withLiveChat={profile.featureFlags.enableLiveChatSupportInMobile}
      />
    );
  }

  return (
    <SailClassicProvider>
      <RootLayer>
        <view.div css={{height: '100vh'}}>
          <ThemedStripeAssistant
            config={config}
            profile={profile}
            observability={observability}
          />
        </view.div>
      </RootLayer>
    </SailClassicProvider>
  );
}

function ThemedStripeAssistant({
  config,
  profile,
  observability,
}: {
  config: AppConfig;
  profile: AppProfile;
  observability: ReturnType<typeof useObservability>;
}) {
  const {theme, overrides} = useConvUITheme(config);

  if (isDotComEntryPoint(config.entryPoint)) {
    return (
      <DotComAssistant
        config={config}
        profile={profile}
        clientFetch={supportFetch}
        inlineDataComponents={DefaultInlineDataComponentRegistry}
        observability={observability}
        theme={theme}
        themeOverrides={overrides}
        withLiveChat
      />
    );
  }

  const AssistantComponent = profile.featureFlags.enableConversationsThreads
    ? ThreadsAssistant
    : StripeAssistant;

  return (
    <AssistantComponent
      config={config}
      profile={profile}
      clientFetch={supportFetch}
      inlineDataComponents={DefaultInlineDataComponentRegistry}
      observability={observability}
      theme={theme}
      themeOverrides={overrides}
      withLiveChat
      withHistory={profile.featureFlags.enableConversationsThreadsHistory}
    />
  );
}

export function ExtractedApp({locale = 'en-US', messages, config}: Props) {
  const {config: appConfig, profile} = useMemo(() => {
    return buildConfig(config, locale);
  }, [config, locale]);

  return (
    <IntlProvider locale={locale} messages={messages} onError={() => {}}>
      <StrictMode>
        <ObservabilityProvider>
          <AppByEntryPoint config={appConfig} profile={profile} />
        </ObservabilityProvider>
      </StrictMode>
    </IntlProvider>
  );
}
